import * as _ from 'underscore';
import { Formatters, Messages } from '../../codegen/Localize';
import { LockStatusHelper, LockUnlockPermissionsState } from '../helpers/LockStatusHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { L10n } from '../L10n/L10n';
import { DatabaseContentType } from '../models/ContentTypes';
import { IControlledPermissionItemInfo } from '../models/resourceInfoParts';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog } from '../services/ConfirmActionDialog';
import { SetDatabaseControlledPermissionsAction } from '../services/SetDatabaseControlledPermissionsAction';

export interface ILockUnlockDatabasePermissionsScope extends ng.IScope {
  showConfirmationDialog: () => void;
  authorizables: Set<any>;
  authorizableType: string;

  editable: boolean;
  editabilityShortMessage: string;
  editabilityLongMessage: string;

  databaseLockState: {
    newStates: LockUnlockPermissionsState[];
  };

  permissionsDialogMessage: string;
  warningMessage: string;

  canLockDatabasePermissions: () => boolean;

  // making this of type LockUnlockDatabasePermissionsState doesn't work because then
  // it can only be assigned enum values, and not the enum itself. Thus, it has to be
  // of type any.
  permissionState: any;

  lockState: LockUnlockPermissionsState;

  percentComplete: number;
  busyUnlocking: boolean;

  getMessageIconClass: () => string;

  willLockDatabases: () => boolean;

  getLockMessage: () => string;
  getDisabledButtonTooltip: () => string;
}

Module.instance().directive('tbLockUnlockDatabasePermissions', [
  '$translate',
  'ConfirmActionDialog',
  'SetDatabaseControlledPermissionsAction',
  ($translate: ng.translate.ITranslateService,
    confirmActionDialog: ConfirmActionDialog,
    SetDatabaseControlledPermissionsAction: SetDatabaseControlledPermissionsAction) => {
    return {
      restrict: 'E',
      replace: true,
      templateUrl: 'lockUnlockDatabasePermissions.html',
      scope: {
        authorizableType: '=',
        authorizables: '=',
        editable: '=',
        editabilityShortMessage: '=',
        editabilityLongMessage: '=',
        lockState: '=',
      },
      link: (scope: ILockUnlockDatabasePermissionsScope) => {
        const databases: Set<IControlledPermissionItemInfo> = getDatabases();
        scope.canLockDatabasePermissions = () => scope.authorizableType === DatabaseContentType.name;

        scope.willLockDatabases = () => {
          return scope.lockState !== LockUnlockPermissionsState.Locked &&
            LockStatusHelper.getCombinedStatusOfAllItems(scope.databaseLockState.newStates)
            === LockUnlockPermissionsState.Locked;
        };

        scope.getMessageIconClass = () => LockStatusHelper.getLockStateMessageIconClass(scope.lockState);

        scope.getLockMessage = () => $translate.instant(LockStatusHelper.getLockMessage(scope.lockState));

        scope.getDisabledButtonTooltip = () => {
          let tooltip: string;
          if (scope.canLockDatabasePermissions()) {
            tooltip = '';
          } else {
            tooltip = Messages.lockDatabasePermissions_disabledTooltip();
          }

          return tooltip;
        };

        scope.percentComplete = 0;
        scope.permissionState = LockUnlockPermissionsState;
        scope.permissionsDialogMessage = $translate.instant('lockDatabasePermissions_dialogMessage',
          {
            DATABASE_COUNT: databases.size(),
            DATABASE_NAME: L10n.elideIfLong(databases.first().name)
          }, L10n.Interpolations.Count);

        scope.warningMessage = Messages.lockDatabasePermissionsWarningMessage({
          DATABASE_COUNT: databases.size(),
          DATABASE_COUNT_LOC: Formatters.formatCount(databases.size())});

        scope.showConfirmationDialog = () => {
          scope.databaseLockState = { newStates: LockStatusHelper.getLockStatusOfAllContainers(databases) };
          confirmActionDialog.open({
            title: $translate.instant('lockDatabasePermissions_dialogTitle'),
            bodyTemplateUrl: 'lockUnlockDatabasePermissionsDialog.html',
            scope: scope,
            position: { reserveHeight: 450 },
            actionName: $translate.instant('save'),
            action: () => {
              return SetDatabaseControlledPermissionsAction.execute(databases, scope.databaseLockState.newStates, scope)
                .then((result: IResult) => {
                  if (!ResultHelper.hasErrors(result)) {
                    updateControlledPermissionsEnabled(scope.databaseLockState.newStates);
                    scope.lockState = LockStatusHelper.getLockState(databases);
                  }
                });
              // TODO: handle error, message $translate.instant("lockDatabasePermissionsError")
            },
            confirmDisabled: () => {
              const enumState: LockUnlockPermissionsState =
                LockStatusHelper.getCombinedStatusOfAllItems(scope.databaseLockState.newStates);
              return enumState === LockUnlockPermissionsState.Mixed || enumState === scope.lockState;
            }
          });
        };

        function updateControlledPermissionsEnabled(updatedPermissionSource: LockUnlockPermissionsState[]) {
          const updatedControlledPermissionEnabled: boolean =
            _.first(updatedPermissionSource) === LockUnlockPermissionsState.Locked;

          scope.authorizables.each((db: any) => {
            if (db.controlledPermissionsEnabled !== updatedControlledPermissionEnabled) {
              db.controlledPermissionsEnabled = updatedControlledPermissionEnabled;
            }
          });
        }

        function getDatabases(): Set<IControlledPermissionItemInfo> {
          return <Set<IControlledPermissionItemInfo>>scope.authorizables;
        }
      }
    };
  }
]);
